// Phase 11 · Step 6: what each couple owes, when, and whether it's late.
// Built from her contract's Section 4 (retainer within 14 days of the Effective Date, balance 30 days
// before the wedding, services may pause when a payment is 7+ days overdue) and Section 2 (extra on-site
// hours at $175 in half-hour steps, invoiced after the event, due in 14 days).
// Shared by the dashboard and the server (countersign creates the schedule). Plain JS, no imports beyond packages.js.
import { normalizeSettings } from "./packages.js";
import { signerEmails } from "./signers.js";

export const DEFAULT_PAYMENT_INSTRUCTIONS = "Reply to this email and I'll send you the payment details.";
const DAY = 86400000;

/** "Jun 12, 2027" or "2027-06-12" → "2027-06-12" (null when it isn't a real date). */
export function ymd(v) {
  const s = String(v || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return null;
  const d = new Date(t);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}
export function addDays(day, n) {
  const [y, m, d] = String(day).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + Number(n || 0))).toISOString().slice(0, 10);
}
export const daysBetween = (from, to) => Math.round((Date.parse(to + "T00:00:00Z") - Date.parse(from + "T00:00:00Z")) / DAY);

const money = n => Math.round(Number(n || 0) * 100) / 100;

/**
 * The payment schedule for an executed (or paper-signed) agreement: the retainer and the balance.
 * effectiveDate: YYYY-MM-DD. Amounts come from the agreement itself (contract.money), so they match what was signed.
 */
export function buildFees({ contract, client, settings, effectiveDate }) {
  const t = normalizeSettings(settings).terms;
  const m = (contract && contract.money) || {};
  const total = money(m.total);
  if (!(total > 0)) return [];
  const retainer = m.retainer != null ? money(m.retainer) : money(total * t.retainerPct / 100);
  const balance = m.balance != null ? money(m.balance) : money(total - retainer);
  const eff = effectiveDate || (contract && contract.effectiveDate);
  const event = ymd(client && client.date);
  const fees = [{ id: "retainer", label: "Retainer (" + t.retainerPct + "%)", amount: retainer, due: eff ? addDays(eff, t.retainerDueDays) : null, paidAt: null, kind: "retainer" }];
  if (balance > 0) fees.push({ id: "balance", label: "Balance (" + (100 - t.retainerPct) + "%)", amount: balance, due: event ? addDays(event, -t.balanceDaysBefore) : (m.balanceDue || null), paidAt: null, kind: "balance" });
  return fees;
}

/** One payment's state on a given day: Paid · Overdue · Due soon (within 7 days) · Pending. */
export function feeStatus(fee, today, settings) {
  const t = normalizeSettings(settings).terms;
  if (fee.paidAt) return { status: "Paid", daysOverdue: 0, pause: false };
  if (!fee.due) return { status: "Pending", daysOverdue: 0, pause: false };
  const late = daysBetween(fee.due, today);
  if (late > 0) return { status: "Overdue", daysOverdue: late, pause: late > t.latePauseDays };
  return { status: -late <= 7 ? "Due soon" : "Pending", daysOverdue: 0, daysUntil: -late, pause: false };
}

/** Totals for a couple, plus the payment that needs attention first. */
export function feeSummary(fees, today, settings) {
  const list = Array.isArray(fees) ? fees : [];
  const rows = list.map(f => ({ ...f, ...feeStatus(f, today, settings) }));
  const total = money(rows.reduce((s, f) => s + Number(f.amount || 0), 0));
  const paid = money(rows.filter(f => f.paidAt).reduce((s, f) => s + Number(f.amount || 0), 0));
  const open = rows.filter(f => !f.paidAt).sort((a, b) => String(a.due || "9999").localeCompare(String(b.due || "9999")));
  return {
    rows, total, paid, outstanding: money(total - paid),
    overdue: rows.filter(f => f.status === "Overdue"),
    pause: rows.some(f => f.pause),
    next: open.find(f => f.status === "Overdue") || open.find(f => f.status === "Due soon") || open[0] || null
  };
}

/** Extra on-site hours: rounded up to the contract's increment, at the overtime rate, due in invoiceDueDays. */
export function overtimeFee(hours, today, settings) {
  const t = normalizeSettings(settings).terms;
  const h = Number(hours);
  if (!(h > 0)) return null;
  const inc = t.overtimeIncrementHrs || 0.5;
  const billed = Math.ceil(h / inc - 1e-9) * inc;
  return {
    id: "overtime-" + today + "-" + Math.round(billed * 100),
    label: "Extra on-site hours (" + billed + (billed === 1 ? " hour)" : " hours)"),
    amount: money(billed * t.overtimeRate), due: addDays(today, t.invoiceDueDays), paidAt: null, kind: "overtime", hours: billed
  };
}

const longDay = day => {
  const [y, m, d] = String(day).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" });
};
const usd = n => "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2, maximumFractionDigits: 2 });

/** A plain reminder in her voice, for her to send from her own email. Never threatening; the pause is her call. */
export function reminderEmail({ client, fee, settings, today }) {
  const s = normalizeSettings(settings);
  const st = feeStatus(fee, today, s);
  const biz = s.businessName || "GhostWriter Events";
  // Phase 12 · Batch C: payment reminders go to whoever signed for the client (the partners, unless she set others).
  const to = signerEmails(client).join(", ");
  const what = fee.label.replace(/\s*\(.*\)$/, "").toLowerCase();
  const L = ["Hi " + ((client && client.names) || "there") + ",", ""];
  if (st.status === "Overdue") L.push("A quick note: the " + what + " of " + usd(fee.amount) + " was due on " + longDay(fee.due) + ", and I haven't seen it come through yet.");
  else L.push("A friendly reminder that the " + what + " of " + usd(fee.amount) + (fee.due ? " is due on " + longDay(fee.due) : " is due") + ".");
  L.push("");
  L.push(String(s.paymentInstructions || "").trim() || DEFAULT_PAYMENT_INSTRUCTIONS);
  L.push("");
  L.push("If it's already on its way, thank you, and please ignore this.");
  L.push("");
  L.push("Warmly,");
  L.push(s.plannerName ? s.plannerName + " · " + biz : biz);
  return { to, subject: biz + " · " + fee.label.replace(/\s*\(.*\)$/, "") + (st.status === "Overdue" ? " (past due)" : " reminder"), body: L.join("\n") };
}
